import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect } from "react";
import { usePopperTooltip } from "react-popper-tooltip";
import isMobile from "is-mobile";
import assert from "assert";
import { ThemePortal } from "../ThemePortal/ThemePortal.mjs";
import { atoms } from "../../css/atoms/atoms.mjs";
import { DefaultTextPropsProvider } from "../private/defaultTextProps.mjs";
import { useSpace } from "../useSpace/useSpace.mjs";
import { useThemeName } from "../useThemeName/useThemeName.mjs";
import { Box } from "../Box/Box.mjs";
import { verticalOffsetBeforeEntrance, translateZ0, background, maxWidth, padding, arrow } from "./TooltipRenderer.css.mjs";
const StaticTooltipContext = createContext(false);
const TooltipTextDefaultsProvider = ({ children }) => {
  const themeName = useThemeName();
  return /* @__PURE__ */ jsx(DefaultTextPropsProvider, { size: themeName === "docs" ? "small" : void 0, weight: "medium", children });
};
const borderRadius = "large";
const TooltipContent = ({
  children,
  opacity,
  arrowProps
}) => /* @__PURE__ */ jsx(
  Box,
  {
    display: "flex",
    position: "relative",
    transition: "fast",
    opacity: opacity === 0 ? 0 : void 0,
    className: opacity === 0 ? verticalOffsetBeforeEntrance : translateZ0,
    children: /* @__PURE__ */ jsx(
      Box,
      {
        boxShadow: "large",
        background: "customDark",
        borderRadius,
        className: [background, maxWidth, translateZ0, padding],
        children: /* @__PURE__ */ jsxs(TooltipTextDefaultsProvider, { children: [
          /* @__PURE__ */ jsx(Box, { position: "relative", zIndex: 1, children }),
          /* @__PURE__ */ jsx(
            Box,
            {
              ...arrowProps,
              borderRadius,
              className: [arrow, background]
            }
          )
        ] })
      }
    )
  }
);
const validPlacements = ["top", "bottom"];
const TooltipRenderer = ({
  id,
  tooltip,
  placement = "top",
  children
}) => {
  assert(
    validPlacements.includes(placement),
    `The 'placement' prop must be one of the following: ${validPlacements.join(", ")}`
  );
  const isStatic = useContext(StaticTooltipContext);
  const [controlledVisible, setControlledVisible] = useState(false);
  const [opacity, setOpacity] = useState(0);
  const { grid, space } = useSpace();
  const {
    visible,
    getTooltipProps,
    setTooltipRef,
    tooltipRef,
    setTriggerRef,
    triggerRef,
    getArrowProps
  } = usePopperTooltip(
    {
      placement,
      trigger: [isMobile() ? "click" : "hover", "focus"],
      visible: isStatic || controlledVisible,
      onVisibleChange: setControlledVisible
    },
    {
      modifiers: [
        {
          name: "preventOverflow",
          options: {
            padding: space.xsmall * grid
          }
        },
        {
          name: "offset",
          options: {
            offset: [0, space.small * grid]
          }
        },
        ...isStatic ? [
          {
            name: "flip",
            options: {
              fallbackPlacements: []
            }
          }
        ] : []
      ]
    }
  );
  useEffect(() => {
    if (visible) {
      const handleKeyDown = ({ key }) => {
        if (key === "Escape") {
          setControlledVisible(false);
        }
      };
      const handleScroll = () => {
        setControlledVisible(false);
      };
      const scrollHandlerOptions = {
        capture: true,
        passive: true
      };
      document.addEventListener("keydown", handleKeyDown);
      document.addEventListener("scroll", handleScroll, scrollHandlerOptions);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
        document.removeEventListener("scroll", handleScroll, scrollHandlerOptions);
      };
    }
  }, [visible]);
  useEffect(() => {
    if (!triggerRef) {
      return;
    }
    if (visible) {
      const handleFocusIn = (event) => {
        if (event.currentTarget !== triggerRef) {
          setControlledVisible(false);
        }
      };
      document.addEventListener("focusin", handleFocusIn);
      return () => {
        document.removeEventListener("focusin", handleFocusIn);
      };
    }
  }, [triggerRef, visible]);
  assert(
    useEffect(() => {
      if (tooltipRef) {
        assert(
          tooltipRef.querySelectorAll("button, input, select, textarea, a").length === 0,
          "For accessibility reasons, tooltips must not contain interactive elements"
        );
      }
    }, [tooltipRef]) === void 0
  );
  useEffect(() => {
    if (!tooltipRef || !visible) {
      return setOpacity(0);
    }
    const timeout = setTimeout(() => setOpacity(100), isMobile() ? 0 : 250);
    return () => clearTimeout(timeout);
  }, [tooltipRef, visible]);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    children({
      triggerProps: {
        tabIndex: 0,
        ref: setTriggerRef,
        "aria-describedby": id
      }
    }),
    triggerRef && /* @__PURE__ */ jsx(ThemePortal, { children: /* @__PURE__ */ jsx(
      "div",
      {
        id,
        role: "tooltip",
        hidden: !visible ? true : void 0,
        className: atoms({
          reset: "div",
          zIndex: "notification",
          pointerEvents: "none",
          display: triggerRef && visible ? void 0 : "none"
        }),
        ...visible ? getTooltipProps({
          ref: setTooltipRef
        }) : null,
        children: /* @__PURE__ */ jsx(TooltipContent, { opacity, arrowProps: getArrowProps(), children: tooltip })
      }
    ) })
  ] });
};
export {
  TooltipRenderer
};
